// frontend/src/components/StripePayment/StripePayment.jsx
import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import { getStripeConfig, createPaymentIntent, confirmPayment } from '../../services/stripeService';
import styles from './StripePayment.module.css';

// Price per day in dollars
const PRICE_PER_DAY = 750;

/**
 * Inner payment form component (must be inside Elements provider)
 */
const PaymentForm = ({ amount, reservationId, onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [paymentSucceeded, setPaymentSucceeded] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);
        setErrorMessage(null);

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    // Return URL for redirect-based payment methods (optional)
                    return_url: `${window.location.origin}/account/payments`,
                },
                redirect: 'if_required', // Only redirect if necessary (e.g., 3D Secure)
            });

            if (error) {
                setErrorMessage(error.message);
            } else if (paymentIntent && paymentIntent.status === 'succeeded') {
                // Notify backend that payment succeeded so it can update the reservation
                try {
                    await confirmPayment(paymentIntent.id, reservationId);
                } catch (confirmErr) {
                    console.warn('Could not confirm payment on server (webhook may handle it):', confirmErr);
                }
                setPaymentSucceeded(true);
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (err) {
            setErrorMessage('An unexpected error occurred. Please try again.');
            console.error('Payment error:', err);
        } finally {
            setIsProcessing(false);
        }
    };

    if (paymentSucceeded) {
        return (
            <div className={styles.successMessage}>
                <span className={styles.successIcon}>✓</span>
                <span className={styles.successText}>Payment Successful!</span>
                <span className={styles.successSubtext}>
                    Your reservation has been paid. Thank you!
                </span>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            {errorMessage && (
                <div className={styles.errorMessage}>{errorMessage}</div>
            )}

            <div className={styles.paymentElement}>
                <PaymentElement
                    options={{
                        layout: {
                            type: 'accordion',
                            defaultCollapsed: false,
                        },
                        paymentMethodOrder: ['card'],
                        wallets: {
                            applePay: 'never',
                            googlePay: 'never',
                        },
                    }}
                />
            </div>

            <button
                type="submit"
                disabled={!stripe || isProcessing}
                className={styles.submitButton}
            >
                {isProcessing ? (
                    <>
                        <span className={styles.spinner}></span>
                        Processing...
                    </>
                ) : (
                    `Pay $${(amount / 100).toLocaleString()}`
                )}
            </button>
        </form>
    );
};

/**
 * Main StripePayment component
 * Handles loading Stripe, creating payment intent, and rendering payment form
 */
const StripePayment = ({ reservationId, dayCount, onPaymentSuccess }) => {
    const [stripePromise, setStripePromise] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [amount, setAmount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const initializePayment = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Get Stripe publishable key
                const config = await getStripeConfig();
                const stripeInstance = loadStripe(config.publishableKey);
                setStripePromise(stripeInstance);

                // Create a PaymentIntent
                const paymentData = await createPaymentIntent(reservationId, dayCount);
                setClientSecret(paymentData.clientSecret);
                setAmount(paymentData.amount);
            } catch (err) {
                console.error('Failed to initialize payment:', err);
                setError(err.response?.data?.message || 'Failed to load payment form. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        initializePayment();
    }, [reservationId, dayCount]);

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <span>Loading payment form...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorMessage}>{error}</div>
        );
    }

    if (!clientSecret) {
        return null;
    }

    // Calculate display amount
    const totalAmount = dayCount * PRICE_PER_DAY;

    return (
        <div className={styles.paymentContainer}>
            <div className={styles.amountDisplay}>
                <div className={styles.amountLabel}>Reservation</div>
                <div className={styles.amountValue}>${totalAmount.toLocaleString()}</div>
            </div>

            <div className={styles.formWrapper}>
                <Elements
                    stripe={stripePromise}
                    options={{
                        clientSecret,
                        appearance: {
                            theme: 'stripe',
                            variables: {
                                colorPrimary: '#2e7d32',
                                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                                borderRadius: '6px',
                            },
                        },
                    }}
                >
                    <PaymentForm amount={amount} reservationId={reservationId} onSuccess={onPaymentSuccess} />
                </Elements>
            </div>
        </div>
    );
};

export default StripePayment;
